import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  type MutableRefObject,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import Modal from '../Modal/Modal';
import { LoaderType, NextgenImage } from '../NextgenImage/NextgenImage';
import { useTranslation } from '../Translation/TranslationProvider';
import { useConfig } from '../Config/ConfigProvider';
import { type VideoPropsType } from './types/propTypes';
import styles from './Video.module.scss';

/**
 * @description Video Provides video component to display the vide with all basic controls.
 * Props ->
 * src    : prop to get the video url
 * poster : prop to get the poster url
 * track  : track details of the video
 */
const Video = forwardRef<HTMLVideoElement, VideoPropsType>(
  (
    {
      src = '',
      poster = '',
      track = {},
      className = '',
      modalSpecs,
      ariaLabelPlayButton,
      ariaLabelShowButton,
      playIconImg,
      ...restProps
    }: VideoPropsType,
    ref,
  ) => {
    const [stop, setStop] = useState(false);
    const [videoDuration, setVideoDuration] = useState<string>('');
    const [showControls, setShowControls] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const videoRef = useRef() as MutableRefObject<HTMLVideoElement>;
    const t = useTranslation();
    const configs = useConfig();

    const playIconUrl = playIconImg ? playIconImg : `${configs.NEXT_ASSETS_BASE_PATH}/images/Play.png`;

    const ariaLabelPlayButtonValue = ariaLabelPlayButton || t('nextgen.Play.ariaLabel');
    const ariaLabelShowButtonValue = ariaLabelShowButton || t('nextgen.ShowModal.ariaLabel');

    useImperativeHandle(ref, () => videoRef.current);
    const handleVideo = () => {
      setStop(!stop);
      if (stop === true) {
        videoRef?.current?.pause();
      } else {
        videoRef?.current?.play();
        setShowControls(true);
      }
    };

    useEffect(() => () => videoRef?.current?.pause(), []);

    const handleLoadedMetadata = () => {
      if (videoRef.current) {
        setVideoDuration((videoRef.current?.duration / 60).toFixed(2));
      }
    };

    const videoContent = (
      <div className={styles['video-container']} data-testid="video-test-id">
        <video
          className={`${styles['video']} ${className}`}
          ref={videoRef}
          onLoadedMetadata={handleLoadedMetadata}
          controls={showControls}
          poster={poster}
          src={src}
          {...restProps}
        >
          <track kind="captions" {...track}></track>
        </video>
        {src ? (
          <div className={styles['play-control']}>
            {!stop ? (
              <div
                onClick={handleVideo}
                onKeyDown={handleVideo}
                role="button"
                aria-label={ariaLabelPlayButtonValue}
                tabIndex={0}
                data-testid="play-button-testid"
              >
                <NextgenImage
                  alt="Play"
                  src={playIconUrl}
                  {...(playIconImg ? {} : { loaderType: LoaderType.WEBFONTS })}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {!stop ? (
          <div className={styles['duration-container']}>
            <span className={styles['duration']}>{videoDuration}</span>
          </div>
        ) : null}
      </div>
    );

    const handleModalVisibility = (visible: boolean, e?: ReactKeyboardEvent | ReactMouseEvent) => {
      setShowModal(visible);
      modalSpecs?.onModalToggle?.(e);
    };

    const getModalContent = () => (
      <>
        <div
          className={styles['modal-wrapper']}
          onClick={(e) => handleModalVisibility(true, e)}
          onKeyDown={(e) => handleModalVisibility(true, e)}
          role="button"
          aria-label={ariaLabelShowButtonValue}
          tabIndex={0}
          data-testid="modal-wrapper"
        >
          <NextgenImage src={poster} alt="Poster" />
          <div className={styles['play-control']}>
            <NextgenImage src={playIconUrl} alt="Play" {...(playIconImg ? {} : { loaderType: LoaderType.WEBFONTS })} />
          </div>
        </div>
        {showModal && (
          <Modal
            headline={modalSpecs?.headline ?? ''}
            primaryButtonLabel="Close"
            onClose={(e) => handleModalVisibility(false, e)}
            onPrimaryButtonClick={(e) => handleModalVisibility(false, e)}
          >
            {videoContent}
          </Modal>
        )}
      </>
    );

    return modalSpecs ? getModalContent() : videoContent;
  },
);
Video.displayName = 'Video';
export default Video;
